import React, { useEffect, useRef } from 'react';
import { Tooltip } from 'bootstrap';
import SectionHeader, { SectionHeaderTypes } from '../Common/SectionHeader'
import useExecFunctionOnBackButton from '../Common/useExecFunctionOnBackButton';

export default function DocList({ folderId = 0, documentList, onSelectDoc, onSelectAllDocs, selected, onDownloadDoc, onSortDocs, sortBy, sortOrder, onUnselectProject }) {
    const tooltipList = useRef([]);
    useEffect(() => initializeTooltips(folderId), [folderId]);
    useEffect(() => handleAddResizeEvent(), []);
    useExecFunctionOnBackButton(() => {
        window.history.go(1);
        onUnselectProject();
    });

    const numberFormatter = new Intl.NumberFormat('en-US', { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 });
    // const dateFormatter = new Intl.DateTimeFormat('en-US');
    const dateTimeFormatter = new Intl.DateTimeFormat('en-US', { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });

    function initializeTooltips(folderId) {
        const tooltipTriggerList = document.querySelectorAll(`.documentRow[data-folder-id="${folderId}"] .fileName[data-bs-toggle="tooltip"]`);
        const newTooltipList = [...tooltipTriggerList].map(el => {
            return { element: el, tooltip: new Tooltip(el) };
        });

        tooltipList.current = newTooltipList;

        return () => {
            // Dispose tooltips.
            newTooltipList.map(t => t.tooltip.dispose());
        };
    }

    function handleAddResizeEvent() {
        function handleResize() {
            const newTooltipList = tooltipList.current.map(x => {
                //if (x.element.offsetWidth >= x.element.scrollWidth) {x.tooltip.disable();}
                //else {x.tooltip.enable();}
                x.tooltip.enable();
                return x;
            });

            tooltipList.current = newTooltipList;
        }

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }

    function handleSortDocs(sortByAndOrder) {
        const [sortByParm, sortOrderParam] = sortByAndOrder.split("-");

        onSortDocs(sortByParm, sortOrderParam);
    }

    let selectedCount = documentList.filter(doc => doc.selected).length;

    let documentTableRows = documentList.length === 0 ? [(<li key="0"><div className="document"><span className="name">No Documents</span></div></li>)] :
        documentList.map(doc => {
            let downloadBtn = (<button className="button" onClick={() => onDownloadDoc(doc.referenceId)}>Download</button>);
            if (selectedCount > 0) {
                downloadBtn = (<button className="button disabled">Download</button>);
            }

            let selectDoc = onSelectDoc && (<span className="select" onClick={() => onSelectDoc(doc.referenceId)}>
                <i className={`text-lg cursor-pointer ${doc.selected ? "dhicon-check-2" : "dhicon-check-empty"}`} />
            </span>);

            let fileSize;
            if (doc.fileSize < 1024) {
                fileSize = `${numberFormatter.format(doc.fileSize)} B`;
            } else if (doc.fileSize < Math.pow(1024, 2)) {
                fileSize = `${numberFormatter.format(doc.fileSize / 1024)} KB`;
            } else if (doc.fileSize < Math.pow(1024, 3)) {
                fileSize = `${numberFormatter.format(doc.fileSize / Math.pow(1024, 2))} MB`;
            } else if (doc.fileSize < Math.pow(1024, 4)) {
                fileSize = `${numberFormatter.format(doc.fileSize / Math.pow(1024, 3))} GB`;
            } else {
                fileSize = `${numberFormatter.format(doc.fileSize / Math.pow(1024, 4))} TB`;
            }

            let fileName = doc.name;
            let fileExtension;
            let fileExtensionIndex = doc.name.lastIndexOf('.');
            if (fileExtensionIndex > 0) {
                fileName = doc.name.substr(0, fileExtensionIndex);
                fileExtension = doc.name.substr(fileExtensionIndex);
            }

            let rows = (<tr key={doc.referenceId} className="documentRow" data-folder-id={folderId}>
                <td className="compact">{selectDoc}</td>
                <td className="w-0 2xl:w-48">{doc.fileCreated ? dateTimeFormatter.format(new Date(doc.fileCreated)) : '-/-/-'}</td>
                <td className="w-fit"><span className="fileName breakWord" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-title={`${doc.folderPath}\\${doc.name}`}>
                    {fileName}{fileExtension}
                </span></td>
                <td className="w-0 2xl:w-48">{doc.fileModified ? dateTimeFormatter.format(new Date(doc.fileModified)) : ''}</td>
                <td className="compact"><span className="size">{fileSize}</span></td>
                <td className="compact">{downloadBtn}</td>
                <td className="smallScreen">
                    <div className="flexbox-flex-row-nowrap">
                        {selectDoc}
                        <div className="document" data-folder-id={folderId}>
                            <div className="flex flex-1"><span className="font-semibold w-17 shrink-0">Created:</span><span>{dateTimeFormatter.format(new Date(doc.fileCreated))}</span></div>
                            {doc.fileModified && (<div className="flex flex-1"><span className="font-semibold w-17 shrink-0">Modified:</span><span>{dateTimeFormatter.format(new Date(doc.fileModified))}</span></div>)}
                            <div className="flex flex-1"><span className="font-semibold w-17 shrink-0">Size:</span><span>{fileSize}</span></div>
                            <span className="fileName" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-title={`${doc.folderPath}\\${doc.name}`}>
                                <span className="name">
                                    {fileName}
                                </span>
                                {fileExtension}
                            </span>
                            <span className="buttonContainer">{downloadBtn}</span>
                        </div>
                    </div>
                </td>
            </tr>);

            return rows;
        });

    let selectAll = onSelectAllDocs && (<span className="select" onClick={() => onSelectAllDocs()}>
        <i className={`text-lg cursor-pointer ${selected ? "dhicon-check-2" : "dhicon-check-empty"}`} />
    </span>);

    let selectBarSmallScreen = (<SectionHeader classes="selectAllSmallScreen m-1 justify-between" sectionHeaderType={SectionHeaderTypes.Secondary}>
        <div className="flex items-center">
            {selectAll}
            <span className="truncate">Select</span>
        </div>
        <div className="text-base">
            <span className="hidden xsm:inline">Sort by</span>
            <select className="bg-drhPalette-secondary " onChange={(e) => handleSortDocs(e.target.value)} value={`${sortBy}-${sortOrder}`}>
                <option className="checked:font-semibold checked:text-white" value='created-desc'>Date desc</option>
                <option className="checked:font-semibold checked:text-white" value='created-asc'>Date asc</option>
                <option className="checked:font-semibold checked:text-white" value='name-desc'>Name desc</option>
                <option className="checked:font-semibold checked:text-white" value='name-asc'>Name asc</option>
                <option className="checked:font-semibold checked:text-white" value='modified-desc'>Modified desc</option>
                <option className="checked:font-semibold checked:text-white" value='modified-asc'>Modified asc</option>
                <option className="checked:font-semibold checked:text-white" value='filesize-desc'>Size desc</option>
                <option className="checked:font-semibold checked:text-white" value='filesize-asc'>Size asc</option>
            </select>
        </div>
    </SectionHeader>);

    let sortIconClass = sortOrder === 'desc' ? 'dhicon-down-open' : 'dhicon-up-open';
    let newSortOrder = sortOrder === 'desc' ? 'asc' : 'desc';

    //let sortableIcon = (<span className="flex"><i className="dhicon-up-open relative -top-1" /><i className="dhicon-down-open relative top-[3px] -left-[22.5px]" /></span>);
    //let sortableColDescClass = "relative -left-[22px]";
    let sortableIcon = (<i className="iconUpDown" />);
    let sortableColDescClass = "";

    let createdHeader = (<th onClick={() => onSortDocs('created', (sortBy === 'created' ? newSortOrder : 'desc'))} className="cursor-pointer">
        <div className="flex">
            {sortBy === 'created' ? (<i className={sortIconClass} />) : (sortableIcon)}
            <span className={sortBy !== 'created' ? sortableColDescClass : ''}>Created</span>
        </div>
    </th>);

    let modifiedHeader = (<th onClick={() => onSortDocs('modified', (sortBy === 'modified' ? newSortOrder : 'desc'))} className="cursor-pointer">
        <div className="flex">
            {sortBy === 'modified' ? (<i className={sortIconClass} />) : (sortableIcon)}
            <span className={sortBy !== 'modified' ? sortableColDescClass : ''}>Modified</span>
        </div>
    </th>);

    let nameHeader = (<th onClick={() => onSortDocs('name', (sortBy === 'name' ? newSortOrder : 'desc'))} className="cursor-pointer">
        <div className="flex">
            {sortBy === 'name' ? (<i className={sortIconClass} />) : (sortableIcon)}
            <span className={sortBy !== 'name' ? sortableColDescClass : ''}>Name</span>
        </div>
    </th>);

    let sizeHeader = (<th onClick={() => onSortDocs('filesize', (sortBy === 'filesize' ? newSortOrder : 'desc'))} className="cursor-pointer">
        <div className="flex">
            {sortBy === 'filesize' ? (<i className={sortIconClass} />) : (sortableIcon)}
            <span className={sortBy !== 'filesize' ? sortableColDescClass : ''}>Size</span>
        </div>
    </th>);

    let docTable = documentTableRows.length === 0 ? undefined : (
        <>
            {selectBarSmallScreen}
            <table className="itemList">
                <thead>
                    <tr>
                        <th>{selectAll}</th>
                        {createdHeader}
                        {nameHeader}
                        {modifiedHeader}
                        {sizeHeader}
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>{documentTableRows}</tbody>
            </table>
        </>);

    return docTable;
}